import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api, getUser } from '@/utils/api';
import { ArrowLeft, Plus, Trash2, Clock, Video, Image, Lock } from 'lucide-react';
import DemoBanner from '@/components/DemoBanner';
import useDemoExpiry from '@/hooks/useDemoExpiry';
import { Switch } from '@/components/ui/switch';
import IssueTypeConfig from '@/components/IssueTypeConfig';

const AdminSettings = () => {
  const navigate = useNavigate();
  const user = getUser();
  useDemoExpiry();
  const isDemoMode = user?.role === 'demo';
  const [issueTypes, setIssueTypes] = useState([]);
  const [newIssueType, setNewIssueType] = useState('');
  const [slaSettings, setSlaSettings] = useState({ target_hours: 48 });
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'demo')) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [issueTypesRes, slaRes] = await Promise.all([
        api.get('/issue-types'),
        api.get('/sla-settings')
      ]);
      setIssueTypes(issueTypesRes.data);
      setSlaSettings(slaRes.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIssueType = async () => {
    if (!newIssueType.trim()) return;

    try {
      await api.post('/issue-types', { name: newIssueType });
      toast.success('Issue type added');
      setNewIssueType('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add issue type');
    }
  };

  const handleDeleteIssueType = async (issueTypeId) => {
    try {
      await api.delete(`/issue-types/${issueTypeId}`);
      toast.success('Issue type deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete issue type');
    }
  };

  const handleUpdateIssueType = async (issueTypeId, updateData) => {
    try {
      await api.patch(`/issue-types/${issueTypeId}`, updateData);
      toast.success('Issue type configuration updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update issue type');
    }
  };

  const getAttachmentInfo = (config) => {
    if (!config?.visible) return null;
    
    let icon = <Image className="h-3 w-3" />;
    if (config.accept_types?.includes('video')) {
      icon = <Video className="h-3 w-3" />;
    }
    
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        <span>{config.max_size_mb}MB</span>
        {config.required && (
          <Badge variant="destructive" className="ml-1 text-xs h-5">Required</Badge>
        )}
      </div>
    );
  };

  const handleUpdateSLA = async () => {
    try {
      await api.put('/sla-settings', { target_hours: parseInt(slaSettings.target_hours) });
      toast.success('SLA settings updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update SLA settings');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    try {
      setChangingPassword(true);
      await api.patch('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>
      <DemoBanner />

      <div className="container mx-auto px-4 sm:px-6 py-6 lg:py-8 max-w-4xl">
        <h1 className="text-3xl font-heading font-bold mb-8">Settings</h1>

        <div className="space-y-6">
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-heading">Issue Types</CardTitle>
              <CardDescription>Manage the types of issues customers can report</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3" data-testid="issue-types-list">
                {issueTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No issue types yet</p>
                ) : (
                  issueTypes.map((type) => (
                    <div key={type.issue_type_id} className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{type.name}</span>
                            {type.is_warranty && (
                              <Badge className="text-xs bg-gray-100 text-gray-500 border border-gray-200">Warranty</Badge>
                            )}
                            {type.is_damage && (
                              <Badge variant="outline" className="text-xs border-orange-300 text-orange-600">Damage</Badge>
                            )}
                            {getAttachmentInfo(type.attachment_config)}
                          </div>
                          {type.attachment_config?.instruction && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{type.attachment_config.instruction}"
                            </p>
                          )}
                          <div className="flex items-center gap-5 mt-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={type.is_warranty || false}
                                onCheckedChange={(checked) => handleUpdateIssueType(type.issue_type_id, { is_warranty: checked })}
                                disabled={isDemoMode}
                                title={isDemoMode ? 'Not available in demo' : undefined}
                                data-testid={`warranty-toggle-${type.issue_type_id}`}
                              />
                              <Label className="text-xs cursor-pointer">Is Warranty Type?</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={type.is_damage || false}
                                onCheckedChange={(checked) => handleUpdateIssueType(type.issue_type_id, { is_damage: checked })}
                                disabled={isDemoMode}
                                title={isDemoMode ? 'Not available in demo' : undefined}
                                data-testid={`damage-toggle-${type.issue_type_id}`}
                              />
                              <Label className="text-xs cursor-pointer">Is Damage Type?</Label>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <IssueTypeConfig issueType={type} onUpdate={handleUpdateIssueType} isDemoMode={isDemoMode} />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteIssueType(type.issue_type_id)}
                            className="text-destructive hover:text-destructive"
                            disabled={isDemoMode}
                            title={isDemoMode ? 'Not available in demo' : undefined}
                            data-testid="delete-issue-type-btn"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="pt-4 border-t border-border">
                <Label htmlFor="new-issue-type">Add New Issue Type</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                  <Input
                    id="new-issue-type"
                    data-testid="new-issue-type-input"
                    placeholder="e.g., Technical Support"
                    value={newIssueType}
                    onChange={(e) => setNewIssueType(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddIssueType()}
                    className="text-base h-11 flex-1"
                  />
                  <Button onClick={handleAddIssueType} data-testid="add-issue-type-btn" className="h-11" disabled={isDemoMode} title={isDemoMode ? 'Not available in demo' : undefined}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-heading">SLA Settings</CardTitle>
              <CardDescription>Set the target closure time for tickets</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="target-hours" className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Target Closure Time (hours)
                  </Label>
                  <Input
                    id="target-hours"
                    type="number"
                    data-testid="target-hours-input"
                    value={slaSettings.target_hours}
                    onChange={(e) => setSlaSettings({ ...slaSettings, target_hours: e.target.value })}
                    className="mt-1.5 w-full sm:max-w-xs text-base h-11"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Tickets open longer than this will be marked as overdue
                  </p>
                </div>
                <Button onClick={handleUpdateSLA} data-testid="update-sla-btn" disabled={isDemoMode} title={isDemoMode ? 'Not available in demo' : undefined}>
                  Update SLA Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle className="text-xl font-heading">Change Password</CardTitle>
                  <CardDescription>Update your admin account password</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 w-full sm:max-w-sm">
                <div>
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    data-testid="current-password-input"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                    className="mt-1.5 text-base h-11"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    data-testid="new-password-input"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                    className="mt-1.5 text-base h-11"
                    placeholder="Enter new password (min 6 chars)"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    data-testid="confirm-password-input"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                    className="mt-1.5 text-base h-11"
                    placeholder="Re-enter new password"
                  />
                </div>
                <Button onClick={handleChangePassword} disabled={changingPassword || isDemoMode} data-testid="change-password-btn" className="w-full sm:w-auto h-11" title={isDemoMode ? 'Not available in demo' : undefined}>
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;